import type { FC, LazyExoticComponent, ReactElement } from "react"
import { Fragment, lazy } from "react"

import { LazyMotion, domAnimation } from "framer-motion"
import { ErrorBoundary } from "react-error-boundary"

import {
    ESpinnerSize,
    ETypographyColor,
    EWaveLoaderVariant,
    ErrorHint,
    HelpHint,
    Loadable,
    Spinner,
    WaveLoader
} from "@/3514/components"
import { ECoachInfoIntegrationMode, type TCoachInfoEntityProps } from "@/3514/entities/coach-info"
import {
    ECoachingSessionsApiMode,
    type TCoachingSessionsEntityProps,
    coachingSessionsEntityLoader
} from "@/3514/entities/coaching-sessions"
import { withNetworkGuard } from "@/3514/guards/hoc"
import { type TUserState, useIsPostSessionModalEnabled, useUser } from "@/3514/hooks"
import { ParticipantLayout } from "@/3514/layouts"
import { EUserRole } from "@/3514/types"

import { ParticipantCoachingSessionsTitle as Title } from "./participant.coaching-sessions.title.component"

function renderWaveLoader(): ReactElement {
    return <WaveLoader withRelativePosition cssIdentifier="pcsp-l" variant={EWaveLoaderVariant.LightBlue} />
}

const LazyHoldsEntity: LazyExoticComponent<FC> = lazy(
        (): Promise<{ default: FC }> =>
            import("./participant.coaching-sessions.holds.entity").then(
                ({ ParticipantCoachingSessionsHoldsEntity: HE }): { default: FC } => ({
                    default: HE
                })
            )
    ),
    LazySessionsEntity: LazyExoticComponent<FC<TCoachingSessionsEntityProps>> = lazy(coachingSessionsEntityLoader),
    LazyCoachInfoEntity: LazyExoticComponent<FC<TCoachInfoEntityProps>> = lazy(
        (): Promise<{ default: FC<TCoachInfoEntityProps> }> =>
            import("@/3514/entities/coach-info").then(
                ({ CoachInfoEntity: CIE }): { default: FC<TCoachInfoEntityProps> } => ({
                    default: CIE
                })
            )
    )

const classes = {
    page: (): string => "px-[20px] pt-[25px] pb-[100px] md:px-[80px] md:pt-[100px]",
    container: (): string => "w-full md:max-w-[700px] md:mx-auto flex flex-col gap-[20px] md:gap-[50px] items-center"
} as const

/**
 * Participant "Coaching Sessions" page.
 * Design {@link https://www.figma.com/design/J6qF3Pa6zeK3udtVWLskjd/Participant?node-id=21023-82384&m=dev}
 */
const Page: FC = (): ReactElement => {
    const { user }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()

    const isPostSessionModalConfigEnabled: boolean = useIsPostSessionModalEnabled()

    return (
        <ParticipantLayout withHtmlScrollBarGutter>
            <div className={classes.page()}>
                <div className={classes.container()}>
                    <Title />

                    <ErrorBoundary fallback={<ErrorHint isVisible />}>
                        <Loadable
                            component={
                                <LazySessionsEntity
                                    apiMode={ECoachingSessionsApiMode.Individual}
                                    user={user}
                                    sessionInfoViewerRole={EUserRole.Participant}
                                    withSwappableSessions={user.areGuestSessionsEnabled}
                                    withCancellableSessions
                                    withAttendanceMarkableSessions={false}
                                    withPostSessionModalOpeners={isPostSessionModalConfigEnabled}
                                    withGiftSessions={false}
                                />
                            }
                            loadingFallback={renderWaveLoader()}
                            withEnforcedSuspense
                        />
                    </ErrorBoundary>

                    <ErrorBoundary fallback={<ErrorHint isVisible />}>
                        <Loadable
                            component={
                                <LazyMotion features={domAnimation}>
                                    <LazyHoldsEntity />
                                </LazyMotion>
                            }
                            loadingFallback={renderWaveLoader()}
                            withEnforcedSuspense
                        />
                    </ErrorBoundary>

                    <ErrorBoundary fallback={<Fragment />}>
                        <Loadable
                            component={
                                <LazyMotion features={domAnimation}>
                                    <LazyCoachInfoEntity
                                        participantId={user.id}
                                        mode={ECoachInfoIntegrationMode.CoachingSessions}
                                        titleConfig={["participantSide.sessionsPage.coachBlock.title"]}
                                    />
                                </LazyMotion>
                            }
                            loadingFallback={<Spinner size={ESpinnerSize.Medium} />}
                            withEnforcedSuspense
                        />
                    </ErrorBoundary>

                    <HelpHint linkColor={ETypographyColor.Blue200} />
                </div>
            </div>
        </ParticipantLayout>
    )
}

Page.displayName = "ParticipantCoachingSessionsPage"

const NetworkGuardedPage: FC = withNetworkGuard(Page)

export { NetworkGuardedPage as ParticipantCoachingSessionsPage }
